"use client";

import { CSSProperties, useState } from "react";
import OtpInput from "react-otp-input";

interface VerifyCodeInputProps {
  onCodeComplete: (value: string) => void;
  disabled?: boolean;
  variant?: "regular" | "narrow" | "small";
}

const OTP_LEN = 6;

const getInputStyle = (
  variant: "regular" | "narrow" | "small"
): CSSProperties => {
  const baseStyle: CSSProperties = {
    boxSizing: "content-box",
    flexShrink: 0,
    overflow: "visible",
    borderRadius: "0.5rem",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
    textAlign: "center",
    fontWeight: 500,
    lineHeight: "2.5rem",
    color: "#000000",
    outline: "none",
    transition: "border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
  };

  switch (variant) {
    case "regular":
      return {
        ...baseStyle,
        paddingLeft: "1.5rem",
        paddingRight: "1.5rem",
        paddingTop: "1.75rem",
        paddingBottom: "1.75rem",
        height: "1.5rem",
        width: "2rem",
        fontSize: "2.25rem",
      };
    case "narrow":
      return {
        ...baseStyle,
        paddingLeft: "0.5rem",
        paddingRight: "0.5rem",
        paddingTop: "1.75rem",
        paddingBottom: "1.75rem",
        height: "1.5rem",
        width: "2rem",
        fontSize: "2.25rem",
      };
    case "small":
      return {
        ...baseStyle,
        padding: "0.75rem",
        height: "1.5rem",
        width: "1.5rem",
        fontSize: "1.875rem",
      };
  }
};

export const VerifyCodeInput = ({
  onCodeComplete,
  disabled = false,
  variant = "regular",
}: VerifyCodeInputProps) => {
  const [otp, setOtp] = useState("");
  const inputStyle = getInputStyle(variant);

  const onCodeChange = (code: string) => {
    setOtp(code);
    if (code.length === OTP_LEN) {
      onCodeComplete(code);
    }
  };

  return (
    <OtpInput
      containerStyle={{
        display: "flex",
        gap: "8px",
        justifyContent: "center",
      }}
      inputType="tel"
      numInputs={OTP_LEN}
      onChange={onCodeChange}
      renderInput={(props) => (
        <input
          {...props}
          style={inputStyle}
          data-cy="verify_code_input"
          disabled={disabled}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#6b7280";
          }}
          onMouseLeave={(e) => {
            if (document.activeElement !== e.currentTarget) {
              e.currentTarget.style.borderColor = "#d1d5db";
            }
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "#6b7280";
            e.currentTarget.style.boxShadow =
              "0 0 0 3px rgba(59, 130, 246, 0.1)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "#d1d5db";
            e.currentTarget.style.boxShadow = "none";
          }}
        />
      )}
      renderSeparator={null}
      shouldAutoFocus
      value={otp}
    />
  );
};
